import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import subscriptionService from '../services/subscriptionService';
import {
  BarChart3,
  MessageSquare,
  Sparkles,
  FileCode,
  Scale,
  Shield,
  Crown,
  Loader2
} from 'lucide-react';

const FEATURE_ICONS = {
  risk_analysis: BarChart3,
  simplification: FileCode,
  chat_ai: MessageSquare,
  recommendations: Sparkles,
  smart_contract: FileCode,
  clause_advisor: Scale
};

const FEATURE_COLORS = {
  risk_analysis: 'text-blue-400 bg-blue-400/10',
  simplification: 'text-purple-400 bg-purple-400/10',
  chat_ai: 'text-green-400 bg-green-400/10',
  recommendations: 'text-pink-400 bg-pink-400/10',
  smart_contract: 'text-orange-400 bg-orange-400/10',
  clause_advisor: 'text-cyan-400 bg-cyan-400/10'
};

const UsageDashboard = ({ compact = false }) => {
  const { token } = useAuth();
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadUsage();
  }, []);

  const loadUsage = async () => {
    try {
      const data = await subscriptionService.getUsage(token);
      setUsage(data);
    } catch (error) {
      console.error('Error loading usage:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-4">
        <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
      </div>
    );
  }

  if (!usage) return null;

  const features = Object.entries(usage.features || {});

  if (compact) {
    // Compact view for dashboard
    return (
      <div className="bg-card border border-border rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-blue-400" />
            Uso de Funciones IA
          </h3>
          {usage.is_premium ? (
            <span className="flex items-center gap-1 text-xs text-yellow-400 bg-yellow-400/10 px-2 py-1 rounded-full">
              <Crown className="w-3 h-3" />
              Premium
            </span>
          ) : (
            <span className="text-xs text-slate-400">Plan Gratuito</span>
          )}
        </div>

        {usage.is_premium ? (
          <p className="text-sm text-emerald-400 flex items-center gap-2">
            <Shield className="w-4 h-4" />
            Acceso ilimitado a todas las funciones
          </p>
        ) : (
          <div className="space-y-2">
            {features.slice(0, 3).map(([key, data]) => {
              const Icon = FEATURE_ICONS[key] || BarChart3;
              const colors = FEATURE_COLORS[key] || 'text-slate-400 bg-slate-400/10';
              const percentage = data.limit > 0 ? ((data.limit - data.remaining) / data.limit) * 100 : 0;

              return (
                <div key={key} className="flex items-center gap-3">
                  <div className={`w-6 h-6 rounded flex items-center justify-center ${colors.split(' ')[1]}`}>
                    <Icon className={`w-3 h-3 ${colors.split(' ')[0]}`} />
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-slate-300">{data.label}</span>
                      <span className="text-slate-400">{data.remaining}/{data.limit}</span>
                    </div>
                    <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${
                          percentage > 80 ? 'bg-red-500' : percentage > 50 ? 'bg-yellow-500' : 'bg-emerald-500'
                        }`}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // Full view
  return (
    <div className="bg-card border border-border rounded-xl">
      <div className="p-4 border-b border-border flex items-center justify-between">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-blue-400" />
          Uso de Funciones IA
        </h2>
        {usage.is_premium ? (
          <span className="flex items-center gap-1 text-sm text-yellow-400 bg-yellow-400/10 px-3 py-1 rounded-full">
            <Crown className="w-4 h-4" />
            Premium Activo
          </span>
        ) : (
          <span className="text-sm text-slate-400 bg-slate-800 px-3 py-1 rounded-full">
            Plan Gratuito
          </span>
        )}
      </div>

      <div className="p-4">
        {usage.is_premium ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Crown className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-xl font-semibold mb-2">¡Acceso Ilimitado!</h3>
            <p className="text-slate-400">
              Disfruta de todas las funciones de IA sin límites
            </p>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {features.map(([key, data]) => {
              const Icon = FEATURE_ICONS[key] || BarChart3;
              const colors = FEATURE_COLORS[key] || 'text-slate-400 bg-slate-400/10';
              const percentage = data.limit > 0 ? ((data.limit - data.remaining) / data.limit) * 100 : 0;
              const isLow = data.remaining <= 1;

              return (
                <div
                  key={key}
                  className={`p-4 rounded-xl border ${
                    isLow ? 'border-red-500/50 bg-red-500/5' : 'border-border bg-slate-900/50'
                  }`}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colors.split(' ')[1]}`}>
                      <Icon className={`w-5 h-5 ${colors.split(' ')[0]}`} />
                    </div>
                    <div>
                      <h4 className="font-medium">{data.label}</h4>
                      <p className="text-xs text-slate-400">
                        {data.period === 'daily' ? 'Límite diario' : 'Límite mensual'}
                      </p>
                    </div>
                  </div>

                  <div className="mb-2">
                    <div className="flex justify-between text-sm mb-1">
                      <span className={isLow ? 'text-red-400' : 'text-slate-300'}>
                        {data.remaining} restantes
                      </span>
                      <span className="text-slate-500">de {data.limit}</span>
                    </div>
                    <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${
                          percentage > 80 ? 'bg-red-500' : percentage > 50 ? 'bg-yellow-500' : 'bg-emerald-500'
                        }`}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>

                  {isLow && (
                    <p className="text-xs text-red-400 mt-2">
                      ⚠️ Pocos usos restantes
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default UsageDashboard;
